/**
 * PersonalRemindersLibraryController — page Bibliothèque de rappels §19.C.7
 * (EPIC-054 / 54.6).
 *
 * Liste tous les `reminder_template` `published` pertinents pour l'org
 * connectée, avec badge d'état et actions inline.
 *
 * Source de vérité : `GET /me/personal-reminders/library`.
 * Mutations :
 *  - `POST /me/personal-reminders` `{templateKey}` — activation
 *    initiale ou réactivation transparente côté service.
 *  - `POST /me/personal-reminders/{id}/dismiss` — désactivation.
 *  - `POST /me/personal-reminders/{id}/reactivate` — sortie de
 *    l'état `dismissed`.
 *
 * Recherche : filtre **client** sur `title` (volume V1 ≈ 10 templates, pas
 * de pagination — D11 §19.C).
 *
 * Convention `<template>` + `data-slot` + `cloneTemplate()` — pas de HTML
 * inline dans le JS (CLAUDE.md règle 8).
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";

const STATE_LABELS = {
    suggested: "Suggéré",
    active: "Actif",
    completed: "Marqué comme fait",
    dismissed: "Désactivé",
};

export default class PersonalRemindersLibraryController extends BaseController {
    static targets = ["search", "loading", "list", "empty", "error", "cardTemplate"];

    connect() {
        this.allEntries = [];
        this.searchQuery = "";
        this.load();
    }

    async load() {
        this.errorTarget.hidden = true;
        try {
            const response = await api.get("/me/personal-reminders/library");
            this.allEntries = Array.isArray(response?.data) ? response.data : [];
            this.render();
        } catch {
            this.loadingTarget.hidden = true;
            this.listTarget.hidden = true;
            this.emptyTarget.hidden = true;
            this.errorTarget.textContent =
                "Impossible de charger la bibliothèque. Réessayez plus tard.";
            this.errorTarget.hidden = false;
        }
    }

    onSearch(event) {
        this.searchQuery = (event.target.value ?? "").trim().toLowerCase();
        this.render();
    }

    render() {
        this.loadingTarget.hidden = true;

        const filtered = this.searchQuery === ""
            ? this.allEntries
            : this.allEntries.filter((e) =>
                (e?.title ?? "").toLowerCase().includes(this.searchQuery),
            );

        if (filtered.length === 0) {
            this.listTarget.hidden = true;
            this.listTarget.replaceChildren();
            this.emptyTarget.hidden = false;
            return;
        }

        this.emptyTarget.hidden = true;
        this.listTarget.replaceChildren();
        for (const entry of filtered) {
            const fragment = this.cardTemplateTarget.content.cloneNode(true);
            this.fillCard(fragment, entry);
            this.listTarget.appendChild(fragment);
        }
        this.listTarget.hidden = false;
    }

    fillCard(fragment, entry) {
        const root = fragment.querySelector('[data-slot="root"]');
        const title = fragment.querySelector('[data-slot="title"]');
        const description = fragment.querySelector('[data-slot="description"]');
        const stateBadge = fragment.querySelector('[data-slot="state-badge"]');
        const mandatoryBadge = fragment.querySelector('[data-slot="mandatory-badge"]');
        const nextDueWrap = fragment.querySelector('[data-slot="next-due"]');
        const nextDueValue = fragment.querySelector('[data-slot="next-due-value"]');
        const detailLink = fragment.querySelector('[data-slot="detail-link"]');
        const primary = fragment.querySelector('[data-slot="primary-action"]');
        const secondary = fragment.querySelector('[data-slot="secondary-action"]');

        root.dataset.templateKey = String(entry.key ?? "");
        title.textContent = entry.title ?? "";
        description.textContent = entry.description ?? "";

        if (entry.mandatory) {
            mandatoryBadge.hidden = false;
        }

        const state = entry.instanceState;
        if (state && STATE_LABELS[state]) {
            stateBadge.textContent = STATE_LABELS[state];
            stateBadge.className = "nm-badge nm-badge--" + this.badgeVariant(state);
            stateBadge.hidden = false;
        }

        if (entry.instanceId) {
            detailLink.href = `/rappels-personnels-detail.html?id=${encodeURIComponent(entry.instanceId)}`;
            detailLink.hidden = false;
        }

        if (entry.nextDueDate && state === "active") {
            nextDueValue.textContent = this.formatDate(entry.nextDueDate);
            nextDueWrap.hidden = false;
        }

        this.bindActions(entry, root, primary, secondary);
    }

    bindActions(entry, root, primary, secondary) {
        const state = entry.instanceState;

        if (state === null || state === undefined) {
            primary.textContent = "Activer";
            primary.hidden = false;
            primary.addEventListener("click", () => this.activateFromTemplate(entry, root));
            return;
        }

        if (state === "suggested") {
            primary.textContent = "Activer";
            primary.hidden = false;
            primary.addEventListener("click", () => this.activateFromTemplate(entry, root));
            if (!entry.mandatory) {
                secondary.textContent = "Ignorer";
                secondary.hidden = false;
                secondary.addEventListener("click", () => this.dismiss(entry, root));
            }
            return;
        }

        if (state === "active" && !entry.mandatory) {
            secondary.textContent = "Désactiver";
            secondary.hidden = false;
            secondary.addEventListener("click", () => this.dismiss(entry, root));
            return;
        }

        if (state === "dismissed") {
            primary.textContent = "Réactiver";
            primary.hidden = false;
            primary.addEventListener("click", () => this.reactivate(entry, root));
        }
    }

    async activateFromTemplate(entry, root) {
        await this.mutate(root, async () => {
            const response = await api.post("/me/personal-reminders", {
                templateKey: entry.key,
            });
            return response?.data ?? null;
        });
    }

    async dismiss(entry, root) {
        if (!entry.instanceId) return;
        await this.mutate(root, async () => {
            const response = await api.post(
                `/me/personal-reminders/${entry.instanceId}/dismiss`,
                {},
            );
            return response?.data ?? null;
        });
    }

    async reactivate(entry, root) {
        if (!entry.instanceId) return;
        await this.mutate(root, async () => {
            const response = await api.post(
                `/me/personal-reminders/${entry.instanceId}/reactivate`,
                {},
            );
            return response?.data ?? null;
        });
    }

    async mutate(root, mutationFn) {
        const buttons = root.querySelectorAll("button");
        for (const btn of buttons) btn.disabled = true;
        try {
            await mutationFn();
            await this.load();
        } catch {
            for (const btn of buttons) btn.disabled = false;
        }
    }

    badgeVariant(state) {
        switch (state) {
            case "active":
                return "success";
            case "suggested":
                return "info";
            case "completed":
                return "muted";
            case "dismissed":
                return "muted";
            default:
                return "info";
        }
    }

    formatDate(isoDate) {
        try {
            const d = new Date(isoDate);
            return d.toLocaleDateString("fr-FR", {
                day: "2-digit",
                month: "long",
                year: "numeric",
            });
        } catch {
            return isoDate;
        }
    }
}
